import { z } from 'zod';
import { CLINIC_SECTIONS } from './access.js';
import {
  AmountRialSchema,
  DateOnlySchema,
  IsoDateTimeSchema,
  optionalText,
  PAGE_LIMIT_MAX,
  RialStringSchema,
  UuidSchema,
} from './common.js';
import {
  CommandSectionSchema,
  JournalEntrySchema,
  PatientBalanceSchema,
  PositiveRialSchema,
  ReasonSchema,
  SectionFilterSchema,
} from './finance.js';

export const SUGGESTED_PAYMENT_PROVIDERS = ['اسنپ‌پی', 'دیجی‌پی', 'تارا'] as const; // names only, never rates
export const PROVIDER_STATUSES = ['active', 'archived'] as const;
export type ProviderStatus = (typeof PROVIDER_STATUSES)[number];

export const PROVIDER_SCHEDULE_KINDS = ['days', 'jalali_months'] as const;
export type ProviderScheduleKind = (typeof PROVIDER_SCHEDULE_KINDS)[number];

export const PROVIDER_SCHEDULE_DAYS_MAX = 366;
export const PROVIDER_STAGES_MAX = 24;
export const PROVIDER_NAME_MAX = 80;
export const PROVIDER_REF_MAX = 64;

export const PROVIDER_RECEIPT_STATUSES = ['active', 'voided'] as const;
export type ProviderReceiptStatus = (typeof PROVIDER_RECEIPT_STATUSES)[number];

export const PROVIDER_RECEIVABLE_STATES = ['open', 'partial', 'collected', 'cancelled'] as const;
export type ProviderReceivableState = (typeof PROVIDER_RECEIVABLE_STATES)[number];

export const PROVIDER_RECEIVABLE_FILTERS = ['open', 'overdue', 'collected', 'cancelled', 'all'] as const;
export type ProviderReceivableFilter = (typeof PROVIDER_RECEIVABLE_FILTERS)[number];

export const PROVIDER_SETTLEMENT_STATUSES = ['active', 'voided'] as const;
export type ProviderSettlementStatus = (typeof PROVIDER_SETTLEMENT_STATUSES)[number];

export const PROVIDER_ALLOCATIONS_MAX = 200;

const BpInput = z.number().int().min(0).max(10_000);

export const ProviderStageSchema = z.object({
  monthOffset: z.number().int().min(1).max(24),
  dayOfMonth: z.number().int().min(1).max(31),
  shareBp: z.number().int().min(1).max(10_000),
});
export type ProviderStage = z.infer<typeof ProviderStageSchema>;

export const ProviderScheduleSchema = z
  .discriminatedUnion('kind', [
    z.object({
      kind: z.literal('days'),
      days: z.number().int().min(0).max(PROVIDER_SCHEDULE_DAYS_MAX),
    }),
    z.object({
      kind: z.literal('jalali_months'),
      stages: z.array(ProviderStageSchema).min(1).max(PROVIDER_STAGES_MAX),
    }),
  ])
  .superRefine((val, ctx) => {
    if (val.kind === 'jalali_months') {
      let prevOffset = 0;
      let totalBp = 0;
      for (let i = 0; i < val.stages.length; i++) {
        const stage = val.stages[i];
        if (!stage) continue;
        if (stage.monthOffset <= prevOffset) {
          ctx.addIssue({
            code: 'custom',
            message: 'ماه هر مرحله باید بعد از مرحله‌ی قبل باشد.',
            path: ['stages', i, 'monthOffset'],
          });
        }
        prevOffset = stage.monthOffset;
        totalBp += stage.shareBp;
      }
      if (totalBp !== 10_000) {
        ctx.addIssue({
          code: 'custom',
          message: 'جمع سهم مرحله‌ها باید دقیقاً ۱۰۰٪ باشد.',
          path: ['stages'],
        });
      }
    }
  });
export type ProviderSchedule = z.infer<typeof ProviderScheduleSchema>;

export const ProviderTermsInputSchema = z.object({
  feeBp: BpInput,
  fixedFeeRial: AmountRialSchema,
  feeTaxBp: BpInput,
  schedule: ProviderScheduleSchema,
});
export type ProviderTermsInput = z.input<typeof ProviderTermsInputSchema>;

export const ProviderInputSchema = z.object({
  name: z.string().trim().min(1, { message: 'نام سرویس را بنویسید.' }).max(PROVIDER_NAME_MAX),
  terms: ProviderTermsInputSchema,
});
export type ProviderInput = z.input<typeof ProviderInputSchema>;

export const ProviderRenameInputSchema = z.object({
  name: z.string().trim().min(1, { message: 'نام سرویس را بنویسید.' }).max(PROVIDER_NAME_MAX),
});
export type ProviderRenameInput = z.input<typeof ProviderRenameInputSchema>;

export const ProviderTermsChangeInputSchema = z.object({
  expectedRevision: z.number().int().min(1),
  terms: ProviderTermsInputSchema,
});
export type ProviderTermsChangeInput = z.input<typeof ProviderTermsChangeInputSchema>;

export const ProviderPreviewQuerySchema = z.object({
  amountRial: PositiveRialSchema,
});
export type ProviderPreviewQuery = z.input<typeof ProviderPreviewQuerySchema>;

const ExternalRefInput = z
  .string()
  .trim()
  .max(PROVIDER_REF_MAX)
  .nullish()
  .transform((v) => (v ? v : null));

export const ProviderPaymentInputSchema = z.object({
  section: CommandSectionSchema,
  amountRial: PositiveRialSchema,
  providerId: UuidSchema,
  termsRevision: z.number().int().min(1),
  externalRef: ExternalRefInput,
  correctsReceiptId: UuidSchema.nullish().transform((v) => v ?? null),
  note: optionalText(300),
  visitId: UuidSchema.nullish().transform((v) => v ?? null),
});
export type ProviderPaymentInput = z.input<typeof ProviderPaymentInputSchema>;

export const ProviderVoidInputSchema = z.object({
  reason: ReasonSchema,
});
export type ProviderVoidInput = z.input<typeof ProviderVoidInputSchema>;

export const ProviderDueChangeInputSchema = z.object({
  dueOn: DateOnlySchema,
  reason: ReasonSchema,
});
export type ProviderDueChangeInput = z.input<typeof ProviderDueChangeInputSchema>;

export const ProviderSettlementInputSchema = z
  .object({
    providerId: UuidSchema,
    section: CommandSectionSchema,
    receivedOn: DateOnlySchema,
    amountRial: PositiveRialSchema,
    trackingRef: ExternalRefInput,
    note: optionalText(300),
    allocations: z
      .array(
        z.object({
          receivableId: UuidSchema,
          amountRial: PositiveRialSchema,
        }),
      )
      .min(1)
      .max(PROVIDER_ALLOCATIONS_MAX),
  })
  .superRefine((val, ctx) => {
    const seen = new Set<string>();
    let totalAllocated = 0n;
    for (let i = 0; i < val.allocations.length; i++) {
      const alloc = val.allocations[i];
      if (!alloc) continue;
      if (seen.has(alloc.receivableId)) {
        ctx.addIssue({
          code: 'custom',
          message: 'هر طلب یک بار',
          path: ['allocations', i, 'receivableId'],
        });
      }
      seen.add(alloc.receivableId);
      try {
        totalAllocated += BigInt(alloc.amountRial);
      } catch {
        // handled by PositiveRialSchema
      }
    }
    try {
      if (BigInt(val.amountRial) !== totalAllocated) {
        ctx.addIssue({
          code: 'custom',
          message: 'جمع تخصیص‌ها باید برابر مبلغ واریز باشد.',
          path: ['allocations'],
        });
      }
    } catch {
      // handled by PositiveRialSchema
    }
  });
export type ProviderSettlementInput = z.input<typeof ProviderSettlementInputSchema>;

export const ProviderReceivablesQuerySchema = z.object({
  providerId: UuidSchema.optional(),
  section: SectionFilterSchema,
  state: z.enum(PROVIDER_RECEIVABLE_FILTERS).default('open'),
  dueFrom: DateOnlySchema.optional(),
  dueTo: DateOnlySchema.optional(),
  cursor: UuidSchema.optional(),
  limit: z.coerce.number().int().min(1).max(PAGE_LIMIT_MAX).default(50),
});
export type ProviderReceivablesQuery = z.input<typeof ProviderReceivablesQuerySchema>;

export const ProviderSettlementsQuerySchema = z.object({
  providerId: UuidSchema.optional(),
  section: SectionFilterSchema,
  from: DateOnlySchema.optional(),
  to: DateOnlySchema.optional(),
  cursor: UuidSchema.optional(),
  limit: z.coerce.number().int().min(1).max(PAGE_LIMIT_MAX).default(50),
});
export type ProviderSettlementsQuery = z.input<typeof ProviderSettlementsQuerySchema>;

// ---- DTOs ----

export const UsableProviderSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  termsRevision: z.number().int(),
});
export type UsableProvider = z.infer<typeof UsableProviderSchema>;

export const ProviderTermsSchema = z.object({
  id: UuidSchema,
  revision: z.number().int(),
  feeBp: z.number().int(),
  fixedFeeRial: RialStringSchema,
  feeTaxBp: z.number().int(),
  schedule: ProviderScheduleSchema,
  createdAt: IsoDateTimeSchema,
});
export type ProviderTerms = z.infer<typeof ProviderTermsSchema>;

export const PaymentProviderSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  status: z.enum(PROVIDER_STATUSES),
  archivedAt: IsoDateTimeSchema.nullable(),
  terms: ProviderTermsSchema,
  openRial: RialStringSchema,
  overdueRial: RialStringSchema,
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type PaymentProvider = z.infer<typeof PaymentProviderSchema>;

export const PaymentProvidersResponseSchema = z.object({
  items: z.array(PaymentProviderSchema),
});
export type PaymentProvidersResponse = z.infer<typeof PaymentProvidersResponseSchema>;

export const UsableProvidersResponseSchema = z.object({
  items: z.array(UsableProviderSchema),
});
export type UsableProvidersResponse = z.infer<typeof UsableProvidersResponseSchema>;

export const ProviderTermsListResponseSchema = z.object({
  items: z.array(ProviderTermsSchema),
});
export type ProviderTermsListResponse = z.infer<typeof ProviderTermsListResponseSchema>;

export const ProviderPreviewSchema = z.object({
  providerId: UuidSchema,
  providerName: z.string(),
  termsRevision: z.number().int(),
  occurredOn: DateOnlySchema,
  grossRial: RialStringSchema,
  feeBp: z.number().int(),
  fixedFeeRial: RialStringSchema,
  feeTaxBp: z.number().int(),
  baseFeeRial: RialStringSchema,
  feeTaxRial: RialStringSchema,
  deductionsRial: RialStringSchema,
  netRial: RialStringSchema,
  schedule: z.array(
    z.object({
      seq: z.number().int(),
      dueOn: DateOnlySchema,
      amountRial: RialStringSchema,
    }),
  ),
});
export type ProviderPreview = z.infer<typeof ProviderPreviewSchema>;

export const ProviderReceiptPublicSchema = z.object({
  id: UuidSchema,
  providerId: UuidSchema,
  providerName: z.string(),
  section: z.enum(CLINIC_SECTIONS),
  patientId: UuidSchema,
  visitId: UuidSchema.nullable(),
  patientPackageId: UuidSchema.nullable(),
  occurredOn: DateOnlySchema,
  grossRial: RialStringSchema,
  externalRef: z.string().nullable(),
  correctsReceiptId: UuidSchema.nullable(),
  status: z.enum(PROVIDER_RECEIPT_STATUSES),
  voidReason: z.string().nullable(),
  voidedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type ProviderReceiptPublic = z.infer<typeof ProviderReceiptPublicSchema>;

export const ProviderPaymentResultSchema = z.object({
  entry: JournalEntrySchema,
  balance: PatientBalanceSchema,
  receipt: ProviderReceiptPublicSchema,
});
export type ProviderPaymentResult = z.infer<typeof ProviderPaymentResultSchema>;

export const ProviderReceivableSchema = z.object({
  id: UuidSchema,
  receiptId: UuidSchema,
  providerId: UuidSchema,
  providerName: z.string(),
  section: z.enum(CLINIC_SECTIONS),
  patientId: UuidSchema,
  patientName: z.string(),
  receiptOn: DateOnlySchema,
  grossRial: RialStringSchema,
  seq: z.number().int(),
  amountRial: RialStringSchema,
  collectedRial: RialStringSchema,
  outstandingRial: RialStringSchema,
  originalDueOn: DateOnlySchema,
  dueOn: DateOnlySchema,
  dueChanged: z.boolean(),
  state: z.enum(PROVIDER_RECEIVABLE_STATES),
  overdue: z.boolean(),
});
export type ProviderReceivable = z.infer<typeof ProviderReceivableSchema>;

export const ProviderReceivablesResponseSchema = z.object({
  items: z.array(ProviderReceivableSchema),
  nextCursor: UuidSchema.nullable(),
  totals: z.object({
    count: z.number().int(),
    openRial: RialStringSchema,
    overdueRial: RialStringSchema,
    collectedRial: RialStringSchema,
  }),
});
export type ProviderReceivablesResponse = z.infer<typeof ProviderReceivablesResponseSchema>;

export const ProviderReceiptDetailSchema = z.object({
  receipt: ProviderReceiptPublicSchema.extend({
    patientName: z.string(),
  }),
  costs: z.object({
    termsId: UuidSchema,
    termsRevision: z.number().int(),
    feeBp: z.number().int(),
    fixedFeeRial: RialStringSchema,
    feeTaxBp: z.number().int(),
    schedule: ProviderScheduleSchema,
    baseFeeRial: RialStringSchema,
    feeTaxRial: RialStringSchema,
    deductionsRial: RialStringSchema,
    netRial: RialStringSchema,
  }),
  stages: z.array(
    ProviderReceivableSchema.extend({
      dueChanges: z.array(
        z.object({
          dueOn: DateOnlySchema,
          reason: z.string(),
          createdAt: IsoDateTimeSchema,
          createdByName: z.string().nullable(),
        }),
      ),
      allocations: z.array(
        z.object({
          settlementId: UuidSchema,
          receivedOn: DateOnlySchema,
          amountRial: RialStringSchema,
          active: z.boolean(),
        }),
      ),
    }),
  ),
  entries: z.array(JournalEntrySchema),
  correctedById: UuidSchema.nullable(),
});
export type ProviderReceiptDetail = z.infer<typeof ProviderReceiptDetailSchema>;

export const ProviderReceiptVoidResultSchema = z.object({
  receipt: ProviderReceiptPublicSchema,
  balance: PatientBalanceSchema,
});
export type ProviderReceiptVoidResult = z.infer<typeof ProviderReceiptVoidResultSchema>;

export const ProviderSettlementSchema = z.object({
  id: UuidSchema,
  providerId: UuidSchema,
  providerName: z.string(),
  section: z.enum(CLINIC_SECTIONS),
  receivedOn: DateOnlySchema,
  amountRial: RialStringSchema,
  trackingRef: z.string().nullable(),
  note: z.string().nullable(),
  status: z.enum(PROVIDER_SETTLEMENT_STATUSES),
  voidReason: z.string().nullable(),
  voidedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  allocations: z.array(
    z.object({
      receivableId: UuidSchema,
      receiptId: UuidSchema,
      seq: z.number().int(),
      patientName: z.string(),
      dueOn: DateOnlySchema,
      amountRial: RialStringSchema,
    }),
  ),
});
export type ProviderSettlement = z.infer<typeof ProviderSettlementSchema>;

export const ProviderSettlementsResponseSchema = z.object({
  items: z.array(ProviderSettlementSchema),
  nextCursor: UuidSchema.nullable(),
  totals: z.object({
    count: z.number().int(),
    activeRial: RialStringSchema,
  }),
});
export type ProviderSettlementsResponse = z.infer<typeof ProviderSettlementsResponseSchema>;
